import { loganLog } from '@RootCommon/utils/logan'
import { owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { deviceHeartBeat } from '@/common/api/omReport'
import { addHeartBeatData } from './deviceHeartBeatData'
import timeoutWorker from '../timeoutWorker'
import { getNetworkInfo } from './network'
import { NETWORK_ERROR_CUSTOM_EVENT } from '@/common/context/NetworkErrorContext'
import { getIsOrderManager, getLoginShopid } from '@RootCommon/utils'
import { LastHeartBeatInfo } from './monitor/type'
import { getOfflineReason, OFFLINE_GAP_TIME, reportHeartBeatGutter, reportOfflineReason } from './monitor'

const DEFAULT_REPORT_GAP_TIME = 30000 // 兜底30s上报一次
const LAST_HEART_BEAT_INFO_LS_KEY = 'last_heart_beat_info'

const NETWORK_TYPE_MAP = {
  '2g': 2,
  '3g': 3,
  '4g': 4,
  'slow-2g': -2,
}

const NO_HEART_BEAT_TIME = 3 * 60 * 1000 // 3min无心跳
let heartBeatFailedstartTime = null // "3min无心跳"的起始计算时间
let lastHeartBeatEndTime = null // 上一次心跳上报结束时间
let lastHeartBeatDuration: number | null = null // 上一次心跳上报耗时
let lastHeartBeatInfo: LastHeartBeatInfo | null = null // 上一次心跳上报信息

/**
 * 心跳上报，如果3min内无心跳，则说明无网或弱网，需要通知electron展示no network页面
 */
export function reportDeviceHeartBeat (
  deviceType: number,
  heartReportType: 1 | 2 = 1,
  needReportHeartBeatData = false, // 是否需要上报心跳数据
  isFirstCall = true,
  isOnlyReportOnce = false,
) {
  const shopId = getLoginShopid()
  /**
   * 同步检查：在函数入口立即检查，避免创建不必要的executeHeartBeat和调度，节省资源
   */
  if (shopId <= 0) {
    return
  }

  const executeHeartBeat = async () => {
    // raptor上报 - 心跳上报触发数
    owlReportMetricsWeb({ device_heart_beat_trigger: 1 })
    loganLog({ msg: '心跳上报触发', shopId: shopId })

    const startTime = Date.now()
    /**
     * OM 首次心跳和5min无心跳都需要上报无心跳原因
     */
    if (getIsOrderManager()) {
      if (isFirstCall) {
        const lastHeartBeatInfo = JSON.parse(localStorage.getItem(LAST_HEART_BEAT_INFO_LS_KEY) || '{}') as LastHeartBeatInfo
        if (lastHeartBeatInfo?.time && startTime - lastHeartBeatInfo.time > OFFLINE_GAP_TIME) {
          reportOfflineReason({ lastHeartBeatInfo })
        }
      } else {
        reportHeartBeatGutter(startTime - lastHeartBeatEndTime) // 心跳间隔上报
        if (startTime - lastHeartBeatEndTime > OFFLINE_GAP_TIME) {
          reportOfflineReason({ lastHeartBeatInfo })
        }
      }
    }

    const connectionData = (navigator as any)?.connection
    const networkInfo = await getNetworkInfo()
    const urlTimeArr = networkInfo.urlTime
    const validDelayTimes = urlTimeArr.filter(item => item.time !== -1)
    const averageDelayTime = validDelayTimes.length > 0 ? validDelayTimes.reduce((a, b) => a + b.time, 0) / validDelayTimes.length : 99999
    let reportGapTime = DEFAULT_REPORT_GAP_TIME
    let isFailed = false
    try {
      const res = await deviceHeartBeat(deviceType, heartReportType, {
        networkType: NETWORK_TYPE_MAP[connectionData?.effectiveType] || -2,
        networkDelay: lastHeartBeatDuration !== null ? Number(lastHeartBeatDuration.toFixed(2)) : Number(averageDelayTime.toFixed(2)),
      })
      reportGapTime = res.reportGapTime ? res.reportGapTime * 1000 : DEFAULT_REPORT_GAP_TIME
      heartBeatFailedstartTime = null
      // 心跳成功，通知在线，隐藏 NetworkError
      window.dispatchEvent(new CustomEvent(NETWORK_ERROR_CUSTOM_EVENT.ONLINE))
      // raptor上报 - 心跳上报成功率
      owlReportMetricsWeb({ device_heart_beat_success_rate: 1 })
      loganLog({ msg: '心跳上报成功' })
    } catch (e) {
      isFailed = true
      if (heartBeatFailedstartTime === null) {
        heartBeatFailedstartTime = Date.now()
      }
      if (Date.now() - heartBeatFailedstartTime >= NO_HEART_BEAT_TIME) {
        // 3min无心跳，展示 NetworkError
        window.dispatchEvent(new CustomEvent(NETWORK_ERROR_CUSTOM_EVENT.OFFLINE))
        loganLog({ msg: '3min无心跳，展示 NetworkError' })
        // 重新计时，防止重复通知electron
        heartBeatFailedstartTime = Date.now()
      }
      // 获取心跳失败原因，复用getOfflineReason
      const reasonCode = await getOfflineReason(lastHeartBeatInfo)
      // raptor上报 - 心跳上报成功率 & 心跳失败原因
      owlReportMetricsWeb({ device_heart_beat_success_rate: 0 }, { reasonCode })
      loganLog({ msg: '心跳上报接口调用失败', error: JSON.stringify(e) })
    }
    const endTime = Date.now()
    const responseTime = endTime - startTime
    lastHeartBeatEndTime = endTime
    lastHeartBeatDuration = responseTime
    if (!isFirstCall) {
      lastHeartBeatInfo = {
        time: endTime,
        isFailed,
        isNetworkConnected: networkInfo.urlTime?.[0]?.httpCode === 200,
      }
      localStorage.setItem(LAST_HEART_BEAT_INFO_LS_KEY, JSON.stringify(lastHeartBeatInfo))
    }

    // 如果reportGapTime为空，则认为心跳失败
    needReportHeartBeatData && addHeartBeatData(startTime, responseTime, isFailed)

    if (!isOnlyReportOnce) {
      timeoutWorker.setTimeout(reportGapTime, () => {
        reportDeviceHeartBeat(deviceType, heartReportType, needReportHeartBeatData, false, false)
      })
    }
  }

  executeHeartBeat()
}
